import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { RiskOperationalSection } from "./RiskOperationalOverview.types";
import { useRiskAreaAssessmentLevel } from "../riskArea/RiskArea.hooks";

// Which of the area's two tables is on screen. Owned by the risk area page rather than the operational tab,
// because the tab row's bulk buttons act on the entity table and so appear only while it is the one showing.
// Only an area assessed at both levels has something to choose between, so for every other area the level
// decides and the stored choice is ignored.
export function useRiskOperationalSection(riskAreaId: string | undefined) {
    const { assessesEntities, assessesOrganisation } = useRiskAreaAssessmentLevel(riskAreaId);

    const [selectedSection, setSelectedSection] = useState(RiskOperationalSection.Entities);

    // A governance tile's deep link names the section its figure came from, so the seeded filter lands on rows the
    // user can actually see. Applied once and then stripped, so a later manual switch is never overridden.
    //
    // Deliberately not useSeedFilterFromUrl, which the two tables use: they are mounted by the tab that the link
    // switches to, so their effect runs on that fresh mount. This hook is owned by the risk area page and stays
    // mounted across tab switches, so it has to watch the param itself — arriving from the governance tab changes
    // only the query string, and an effect keyed on readiness alone would never fire again.
    const [searchParams, setSearchParams] = useSearchParams();
    const sectionParam = searchParams.get("section");
    useEffect(() => {
        if (sectionParam === null) {
            return;
        }

        const section = Number(sectionParam);
        if (section === RiskOperationalSection.Entities || section === RiskOperationalSection.Organisation) {
            setSelectedSection(section);
        }

        // Off the live params, not the render's snapshot: the table mounting alongside strips its own filter params
        // in a child effect, which runs first, and a stale snapshot here would put them back.
        setSearchParams(
            (current) => {
                const next = new URLSearchParams(current);
                next.delete("section");
                return next;
            },
            { replace: true }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sectionParam]);

    const canSwitchSections = assessesEntities && assessesOrganisation;
    const shownSection = canSwitchSections ? selectedSection : assessesEntities ? RiskOperationalSection.Entities : RiskOperationalSection.Organisation;

    return {
        canSwitchSections,
        selectedSection: shownSection,
        onSelectSection: setSelectedSection,
        // Both levels are also tested, not just the section, so nothing renders until the areas list resolves
        // and says which levels the area actually assesses.
        showEntities: assessesEntities && shownSection === RiskOperationalSection.Entities,
        showOrganisation: assessesOrganisation && shownSection === RiskOperationalSection.Organisation,
    };
}

export interface RiskOperationalSectionState {
    canSwitchSections: boolean;
    selectedSection: RiskOperationalSection;
    onSelectSection: (section: RiskOperationalSection) => void;
    showEntities: boolean;
    showOrganisation: boolean;
}
